import { Box, Button, Grid, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { InfoIcon, Trash2 } from 'lucide-react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import React, { useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useMFAttachedDocumentsContext } from '~/microfrontend/hooks/useMFModuleNameContext';
import { Gallery, IGalleryRef } from '~/widgets/components/Gallery';
import { ImagePreview } from '~/widgets/components/ImagePreview';
import EmptyImagesIcon from '~assets/images/empty/emptyImages.svg?react';
import { FileDropzone } from '~components/FileDropzone';
import { EmptyContent } from '~components/reuseable/EmptyContent';
import { AttachedFilesStore } from '~modules/attachedFiles/AttachedFilesStore';
import {
  AttachedImagesDeletionForm,
  AttachedImagesDeletionFormField,
} from '~modules/attachedFiles/forms/AttachedImagesDeletionForm';
import { UploadedFileModel } from '~modules/attachedFiles/models/UploadedFileModel';
import { DocumentFixedTypeModel } from '~modules/fileTypes/models/DocumentFixedTypeModel';
import { FileFormatModel } from '~modules/fileTypes/models/FileFormatModel';

interface IImagesWidgetSectionProps {
  documentFixedType?: DocumentFixedTypeModel;
  fileFormats: FileFormatModel[];
  onRefreshFixedTypesList: () => void;
}

export const ImagesWidgetSection: React.FC<IImagesWidgetSectionProps> = observer((props) => {
  const attachedFilesStore = useLocalObservable(() => new AttachedFilesStore());
  const galleryRef = useRef<IGalleryRef>(null);

  const { documentId, documentTitle, documentTypeId, mode, readonly, notShowFixTypeLabels, withoutFixedTypes, title } =
    useMFAttachedDocumentsContext();

  const { documentFixedType, fileFormats, onRefreshFixedTypesList } = props;

  // Статус readonly в основном определяется из родительского проекта,
  // но для документов Эксплуатации подменных а/м этот статус определяется из documentFixedType
  const currentReadonlyStatus = documentFixedType?.isReadOnly || readonly;

  const { t } = useTranslation('common');

  const methods = useForm<AttachedImagesDeletionForm>({
    mode: 'onChange',
    defaultValues: { ...AttachedImagesDeletionForm.create() },
  });

  const { control, setValue, getValues, reset } = methods;

  const imagesToDelete = useWatch({ control, name: AttachedImagesDeletionFormField.imagesToDelete });
  const isExistSelectedImage = useMemo(() => Object.values(imagesToDelete).some(Boolean), [imagesToDelete]);

  const fixedTypeId = documentFixedType?.fixedType?.id;
  const fixedTypeDescr = documentFixedType?.fixedType?.descr;
  const fixedTypeNotes = documentFixedType?.fixedType?.notes;

  const { palette } = useTheme();

  //Effects

  useEffect(() => {
    attachedFilesStore.getAttachedDocuments(mode, documentTypeId, documentId, documentTitle, fixedTypeId);
  }, [fixedTypeId]);

  useEffect(() => {
    if (!attachedFilesStore.uploadedFiles) {
      return;
    }

    const preparedValues = attachedFilesStore.prepareImagesToDeletionFormValues(attachedFilesStore.uploadedFiles);

    // Автозаполнение формы
    reset(preparedValues);
  }, [attachedFilesStore.uploadedFiles]);

  // Handlers

  const handleRefreshFilesList = () => {
    attachedFilesStore.getAttachedDocuments(mode, documentTypeId, documentId, documentTitle, fixedTypeId);
  };

  const handleOpenGallery = (fileId?: number | null) => {
    galleryRef.current?.openGallery(fileId);
  };

  const handleDeleteFile = (file: UploadedFileModel) => {
    attachedFilesStore.deleteUploadedFile(file, onRefreshFixedTypesList);
  };

  const handleDeleteFiles = () => {
    attachedFilesStore.deleteAttachedImages(getValues(), () => {
      onRefreshFixedTypesList();
      handleRefreshFilesList();
    });
  };

  const handleToggleSelection = () => {
    Object.keys(imagesToDelete).forEach((key) => {
      setValue(`${AttachedImagesDeletionFormField.imagesToDelete}.${key}`, !isExistSelectedImage);
    });
  };

  //Renders

  const renderDropZone = (documentFixedType?: DocumentFixedTypeModel) => {
    const disabled = documentFixedType && !documentFixedType.isEnabled;

    if (disabled || currentReadonlyStatus) {
      return;
    }

    return (
      <FileDropzone
        attachedFilesStore={attachedFilesStore}
        maxFiles={documentFixedType?.isMulti ? 20 : 1}
        documentId={documentId}
        documentTitle={documentTitle}
        documentTypeId={documentTypeId}
        fixedType={documentFixedType?.fixedType}
        fileMode={mode}
        required={documentFixedType?.isRequired}
        fileFormats={fileFormats}
        onUploadFiles={onRefreshFixedTypesList}
      />
    );
  };

  const renderSelectionText = () => {
    if (isExistSelectedImage) {
      return t('button.cancelSelection');
    }

    return t('button.selectAll');
  };

  const renderFixedTypeDescr = () => {
    if (!fixedTypeDescr) {
      return;
    }

    if (notShowFixTypeLabels) {
      return;
    }

    const showNotesTooltip = !withoutFixedTypes && fixedTypeId !== -1; // -1 = "Без группы"

    return (
      <Stack direction="row" gap={1} alignItems="center">
        <Typography variant="subtitle2">{fixedTypeDescr}</Typography>
        {showNotesTooltip && (
          <Tooltip
            title={fixedTypeNotes}
            componentsProps={{
              tooltip: {
                sx: { textAlign: 'left' },
              },
            }}
          >
            <InfoIcon size={24} style={{ flexShrink: 0 }} color={palette.custom.icon.gray} />
          </Tooltip>
        )}
      </Stack>
    );
  };

  const renderControlButtons = () => {
    if (currentReadonlyStatus || !documentFixedType?.hasFiles) {
      return;
    }

    return (
      <Grid container alignItems="center">
        <Grid item xs>
          {title}
        </Grid>
        <Grid item>
          <Stack direction="row" gap={1} ml="auto">
            <Button variant="text" onClick={handleToggleSelection}>
              {renderSelectionText()}
            </Button>
            <IconButton onClick={handleDeleteFiles} disabled={!isExistSelectedImage}>
              <Trash2 color={isExistSelectedImage ? palette.error.main : palette.custom.icon.gray} />
            </IconButton>
          </Stack>
        </Grid>
      </Grid>
    );
  };

  const renderImages = () => {
    if (!attachedFilesStore.uploadedFiles.length && currentReadonlyStatus) {
      return <EmptyContent IconComponent={<EmptyImagesIcon />} title={t('empty.images.title')} />;
    }

    return attachedFilesStore.uploadedFiles?.map((file) => (
      <Grid key={file.fileId} item>
        <ImagePreview
          file={file}
          readonly={currentReadonlyStatus}
          onOpenGallery={() => handleOpenGallery(file.fileId)}
          onDelete={handleDeleteFile}
        />
      </Grid>
    ));
  };

  const renderTitle = () => {
    if (documentFixedType?.hasFiles && documentFixedType?.isEnabled) {
      return null;
    }

    return (
      <Box pl={2} pt={1}>
        {title}
      </Box>
    );
  };

  return (
    <>
      <Gallery ref={galleryRef} attachedFilesStore={attachedFilesStore} />
      <Stack direction="column" gap={1}>
        {currentReadonlyStatus && title}
        <Stack direction="column">
          {renderControlButtons()}
          {renderFixedTypeDescr()}
        </Stack>
        <FormProvider {...methods}>
          <Grid container spacing={2} minHeight={100}>
            {renderImages()}
            {renderTitle()}
            <Grid item xs={12}>
              {renderDropZone(documentFixedType)}
            </Grid>
          </Grid>
        </FormProvider>
      </Stack>
    </>
  );
});
